import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoTableCell, RobotoTableRow } from "@/shared/components";
import { FormattedDateTime } from "@/shared/datetime";
import {
  CollectionChangeRecord,
  CollectionChangeSet,
} from "@/shared/domain/collections";
import { useCollectionChanges } from "@/shared/domain/collections/hooks/useCollectionChanges";

import { useCollectionOutletContext } from "./useCollectionOutletContext";

type RowsPerPage = 10 | 25 | 50;
const initialRowsPerPage: RowsPerPage = 10;

export const CollectionAudit: React.FC = () => {
  const { collection_id } = useCollectionOutletContext();
  const theme = useTheme();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] =
    React.useState<RowsPerPage>(initialRowsPerPage);

  const {
    data: allChanges = [],
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
  } = useCollectionChanges(collection_id);

  React.useEffect(() => {
    const needed = (page + 1) * rowsPerPage;
    if (allChanges.length < needed && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [
    page,
    rowsPerPage,
    allChanges.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  function formatChangeSet(changeset: CollectionChangeSet) {
    const changeGroup = [] as React.ReactElement[];

    if (changeset.added_resources.length > 0) {
      for (let idx = 0; idx < changeset.added_resources.length; ++idx) {
        changeGroup.push(
          <p style={{ color: theme.palette.success.main }}>
            + {changeset.added_resources[idx].resource_id}
          </p>,
        );
      }
    }

    if (changeset.removed_resources.length > 0) {
      for (let idx = 0; idx < changeset.removed_resources.length; ++idx) {
        changeGroup.push(
          <p style={{ color: theme.palette.error.main }}>
            - {changeset.removed_resources[idx].resource_id}
          </p>,
        );
      }
    }

    if (changeset.added_tags.length > 0) {
      for (let idx = 0; idx < changeset.added_tags.length; ++idx) {
        changeGroup.push(
          <p style={{ color: theme.palette.success.main }}>
            + {changeset.added_tags[idx]}
          </p>,
        );
      }
    }

    if (changeset.removed_tags.length > 0) {
      for (let idx = 0; idx < changeset.removed_tags.length; ++idx) {
        changeGroup.push(
          <p style={{ color: theme.palette.error.main }}>
            - {changeset.removed_tags[idx]}
          </p>,
        );
      }
    }

    if (Object.keys(changeset.field_changes).length > 0) {
      const fields = Object.keys(changeset.field_changes);
      for (let idx = 0; idx < fields.length; ++idx) {
        changeGroup.push(
          <p style={{ color: theme.palette.success.main }}>
            + {fields[idx]}:{" "}
            {changeset.field_changes[fields[idx]] as unknown as string}
          </p>,
        );
      }
    }

    return changeGroup;
  }

  const pageData: CollectionChangeRecord[] = allChanges.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  const endIndex = (page + 1) * rowsPerPage;

  return (
    <Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Version</RobotoTableCell>
              <RobotoTableCell>Created</RobotoTableCell>
              <RobotoTableCell>Created By</RobotoTableCell>
              <RobotoTableCell>Changeset</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isPending || (isFetchingNextPage && pageData.length === 0) ? (
              <>
                {Array.from({ length: 3 }, (_, i) => (
                  <TableRow key={`row-${i}`}>
                    {Array.from({ length: 4 }, (_, j) => (
                      <RobotoTableCell key={`cell-${j}`}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          height={30}
                          sx={{ mt: 1, mb: 1 }}
                        />
                      </RobotoTableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {pageData.length > 0 ? (
                  <>
                    {pageData.map((changeset) => {
                      return (
                        <React.Fragment
                          key={`from_version=${changeset.from_version} to_version=${changeset.to_version}`}
                        >
                          <RobotoTableRow>
                            <RobotoTableCell>
                              {changeset.to_version}
                            </RobotoTableCell>
                            <RobotoTableCell>
                              <FormattedDateTime value={changeset.applied} />
                            </RobotoTableCell>
                            <RobotoTableCell>
                              {changeset.applied_by}
                            </RobotoTableCell>
                            <RobotoTableCell sx={{ maxWidth: 400 }}>
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {formatChangeSet(changeset.change_set)}
                              </pre>
                            </RobotoTableCell>
                          </RobotoTableRow>
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  <RobotoTableRow>
                    <RobotoTableCell
                      style={{
                        paddingBottom: theme.spacing(2),
                        paddingTop: theme.spacing(2),
                      }}
                      colSpan={4}
                    >
                      <Alert severity="info">
                        There haven&apos;t been any changes to this collection
                        yet.
                      </Alert>
                    </RobotoTableCell>
                  </RobotoTableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={hasNextPage ? -1 : allChanges.length}
        nextIconButtonProps={{
          disabled: !hasNextPage && endIndex >= allChanges.length,
        }}
        rowsPerPageOptions={[10, 25, 50]}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value) as RowsPerPage);
          setPage(0);
        }}
        labelRowsPerPage={"Rows per page:"}
      />
    </Box>
  );
};
